import React from 'react';

import Input from './Input';
import PropTypes from 'prop-types';
import * as R from 'ramda';

import { hasValidFileExtension, formatBytes } from '../../support/utils';

import classNames from 'classnames';

const TOGGLE_FIELDS = [
  {
    label: 'Use URL',
    key: 'url',
  },
  {
    label: 'Upload Image',
    key: 'file',
  },
];

export const isImageSquare = (imageFile) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = window.URL.createObjectURL(imageFile);
    image.onload = () => resolve(image.height === image.width);
    image.onerror = reject;
  });
};

/**
 * Generic Image Uploader Component (Work In Progress)
 */
class ImageUploader extends React.PureComponent {
  static defaultProps = {
    enableFileToggle: true,
    enablePreview: false,
    onChange: R.indentiy,
  };
  static propTypes = {
    /**
     * Input component description
     */
    description: PropTypes.string,
    /**
     * Enable file toggle
     */
    enableFileToggle: PropTypes.bool,
    /**
     * Enable image preview
     */
    enablePreview: PropTypes.bool,
    /**
     * props to pass to input when your input type is 'file'
     */
    fileProps: PropTypes.object,
    /**
     * props to pass to input
     */
    inputProps: PropTypes.object,
    /**
     * props to pass to input when your input type is 'url'
     */
    urlProps: PropTypes.object,
    /**
     * max size for image upload
     */
    maxSize: PropTypes.number,
    /**
     * min size for image upload
     */
    minSize: PropTypes.number,
    /**
     * Input component footnote
     */
    footnote: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    /**
     * Input onChange callback
     */
    onChange: PropTypes.func,
    /**
     * Input component title
     */
    title: PropTypes.string,
    /**
     * Input component tooltip
     */
    tooltip: PropTypes.string,
    /**
     * Pass data-testid to input
     */
    testid: PropTypes.string,
    /**
     * className applied to the outermost div of this component
     */
    className: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    /**
     * list of valid file extensions that can be uploaded. If this prop is empty, file extension vaidation will not be performed.
     */
    validFileExtensions: PropTypes.arrayOf(PropTypes.string),
    /**
     * Only allow images with square dimensions
     */
    enforceSquare: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      active: props.enableFileToggle ? 'url' : 'file',
      preview: null,
      isFileExtensionValid: true,
      fileSize: null,
      imageIsSquare: null,
    };
  }

  onImageUpload = async (e) => {
    const { validFileExtensions, enforceSquare } = this.props;
    if (!e.target.files?.length) {
      return;
    }
    const file = e.target.files[0];
    const fileName = e.target.value;
    const fileSize = file.size;

    const state = { fileName, fileSize };

    state.isFileExtensionValid = !validFileExtensions || hasValidFileExtension(fileName, validFileExtensions);

    if (enforceSquare && state.isFileExtensionValid) {
      state.imageIsSquare = await isImageSquare(file);
    }

    if (this.props.enablePreview) {
      state.preview = window.URL.createObjectURL(file);
    }

    this.setState(state);

    setTimeout(() => {
      this.props.onChange({
        file,
        preview: this.state.preview,
      });
    });
  };

  onImageUrlChange = (e) => {
    this.setState({
      file: null,
      preview: e.value,
    });
    this.props.onChange({
      preview: this.state.preview,
    });
  };

  getToggleControl = (enableFileToggle) => {
    if (!enableFileToggle) {
      return null;
    }

    return (
      <ul className="image-uploader__toggle h_mx-0 h_p-0 h_fw-500">
        {TOGGLE_FIELDS.map((item) => {
          return (
            <li
              className={classNames('image-uploader__toggle-item h_d-ib h_cur-p', {
                'image-uploader__toggle-item--active': this.state.active === item.key,
              })}
              key={item.key}
              onClick={() => {
                this.setState({
                  active: item.key,
                  preview: this.refs.input.getValue(),
                  fileName: null,
                  isFileExtensionValid: null,
                  imageIsSquare: null,
                  fileSize: null,
                });
              }}
            >
              {item.label}
            </li>
          );
        })}
      </ul>
    );
  };

  getPreview(enablePreview) {
    if (!enablePreview || !this.state.preview) {
      return null;
    }

    return (
      <div className="image-uploader__preview h_pos-r">
        <img className="image-uploader__preview-image h_w-100" src={this.state.preview} ref="image" />
      </div>
    );
  }

  getFileErrorMessages = () => {
    const { validFileExtensions, maxSize, enforceSquare } = this.props;
    const { isFileExtensionValid, fileSize, imageIsSquare } = this.state;
    const maxImageSizeExceeded = maxSize && fileSize ? fileSize > maxSize : false;
    const imageFileType = /(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(validFileExtensions[0]);
    const fileTypeName = imageFileType ? 'file' : 'image';

    let errorText = '';
    // this design renders one error message at a time, starting with largest error first.
    if (isFileExtensionValid === false) {
      errorText = `Wrong file format. Please upload a valid file ending in .${validFileExtensions.join(' or .')}.`;
    } else if (maxImageSizeExceeded) {
      errorText = `The ${fileTypeName} you uploaded is too big. Please upload an ${fileTypeName} less than ${formatBytes(
        maxSize,
        0
      )}.`;
    } else if (enforceSquare && imageIsSquare === false) {
      errorText = 'The image you uploaded is not a square. Please upload a square image.';
    }
    return (
      (errorText.length && <span className="input-control__warning h_ta-end h_d-b h_c-error">{errorText}</span>) || null
    );
  };

  render() {
    const { urlProps, fileProps, inputProps, enableFileToggle, enablePreview, testid, title, className } = this.props;
    const isFile = this.state.active === 'file';

    let activeProps = {
      type: this.state.active,
      testid: testid || 'image-uploader',
      value: this.state.fileName,
      i13nLabel: title || 'Image Uploader',
      ...inputProps,
    };
    if (isFile) {
      activeProps = R.merge(activeProps, {
        ...fileProps,
        multiple: false,
        onChange: this.onImageUpload,
      });
    } else {
      activeProps = R.merge(activeProps, {
        placeholder: 'Type Your Image URL',
        ...urlProps,
        onChange: this.onImageUrlChange,
      });
    }

    return (
      <div className={classNames('image-uploader', className)} data-testid={testid + '-image-uploader'}>
        {this.getToggleControl(enableFileToggle)}
        <Input {...activeProps} ref="input" />
        {this.getPreview(enablePreview)}
        {isFile && this.getFileErrorMessages()}
      </div>
    );
  }
}

export default ImageUploader;
